import React, { useState, useCallback, useContext } from 'react';
import * as yup from 'yup';
import clsx from 'clsx';
import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';

import { extractErrorMessage } from '../../../../Utils/Errors/Errors';
import { moneyWithDots } from '../../../../Utils/Format/MoneyFormat';
import { submitExternalForm } from '../../../../Utils/Misc/Form';
import { COUPON_VALUES } from '../../../../Utils/enums';

import { AlertsDispatchContext } from '../../../../Contexts/AlertsContext';

import { SlideUpTransition } from '../../../../Components/Transitions/Transitions';
import BaseDialog from '../../../../Components/Dialogs/BaseDialog';
import BaseButton from '../../../../Components/Buttons/BaseButton';
import { ProductType } from '../../myDebts_enums';
import ControlledCurrencyInput from '../../../../Components/Controlled/ControlledCurrencyInput';
import { DeferredItemAction } from '../../DeferredDebtSummary/DeferredDebtSummary.GDC';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const couponValueSchema = maxValue => {
  const minSchema = yup
    .number()
    .typeError('Ingresa el valor del cupón')
    .min(
      COUPON_VALUES.MIN_VALUE,
      `Debe ser mayor que ${moneyWithDots(COUPON_VALUES.MIN_VALUE)}`
    )
    .required('Ingresa el valor del cupón');

  const maxSchema = yup
    .number()
    .typeError('Ingresa el valor del cupón')
    .max(maxValue, `Debe ser menor o igual que ${moneyWithDots(maxValue)}`);

  const schema = yup.object({
    value: maxValue ? minSchema.concat(maxSchema) : minSchema
  });

  return schema;
};

const ValuePartialPaymentStep = props => {
  const {
    open,
    title,
    onClose,
    authToken,
    setNextModal,
    requestCallback,
    step,
    description,
    isParentLoading = false,
    toDeferreds = false,
    addDeferreds = undefined
  } = props;

  const { maxValue, currentDebtValueExist, ...dataRest } = step.data;

  const isMobileSize = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  // State
  const [loading, setLoading] = useState(false);

  // Context
  const setAlert = useContext(AlertsDispatchContext);
  const classes = useStyles();

  // Form
  const { control, errors, handleSubmit, watch } = useForm({
    resolver: yupResolver(couponValueSchema(maxValue)),
    mode: 'onBlur'
  });

  const partialPaymentValue = Number(watch(['value']).value);

  const generateButtonClickHandler = useCallback(() => {
    if (toDeferreds) {
      addDeferreds(
        DeferredItemAction.deferred,
        dataRest.deferredId,
        partialPaymentValue
      );
      onClose();
    } else {
      submitExternalForm('Partial_payment_query_form');
    }
  }, [
    addDeferreds,
    dataRest.deferredId,
    onClose,
    partialPaymentValue,
    toDeferreds
  ]);

  // * FORM HANDLERS
  const onSubmit = useCallback(
    async values => {
      setLoading(true);
      const { value } = values;

      const response = await requestCallback(authToken, {
        value,
        productTypeIds: [ProductType.FinancingServices],
        ...dataRest
      });

      if (response.success) {
        if (setNextModal) {
          setNextModal({
            type: 'generation',
            data: {
              ...response.data.data,
              ...{ value }
            }
          });
        }
        return;
      }
      setAlert({
        type: 'error',
        message: extractErrorMessage(response).message
      });
      setLoading(false);
      return;
    },
    [setAlert, setNextModal, authToken, requestCallback, dataRest]
  );

  //* COMPONENTS
  const renderActions = useCallback(() => {
    const actionText = toDeferreds ? 'Agregar' : 'Generar';

    return (
      <>
        <BaseButton
          onClick={onClose}
          variant="outlined"
          color="primary"
          size="small"
        >
          Cancelar
        </BaseButton>
        <BaseButton
          id="ConfirmationDialog_button_submit"
          form="Partial_payment_query_form"
          color="primary"
          size="small"
          type="submit"
          onClick={generateButtonClickHandler}
          disabled={loading || Boolean(errors.value)}
        >
          {actionText}
        </BaseButton>
      </>
    );
  }, [loading, onClose, errors.value, toDeferreds, generateButtonClickHandler]);

  const renderContent = useCallback(() => {
    const noCurrentDebtMaxValueSetted = !maxValue && !currentDebtValueExist;
    const showTextSkeleton = isParentLoading || noCurrentDebtMaxValueSetted;

    return (
      <div id="ContractStep_div_container" className={classes.content}>
        <div className={clsx(classes.dialogDescription, classes.marginBottom)}>
          {description || (
            <Typography>
              Digita el valor que deseas abonar a la deuda:
            </Typography>
          )}
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          id="Partial_payment_query_form"
          className={classes.contractForm}
        >
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item className={classes.textHeight}>
              {!isParentLoading && maxValue > 0 && (
                <Typography className={classes.maxValueText}>
                  Valor <span className={classes.lightBold}>máximo</span> a
                  abonar:{' '}
                  <span className={classes.lightBold}>
                    {moneyWithDots(maxValue)}
                  </span>
                </Typography>
              )}
              {showTextSkeleton && <Skeleton className={classes.skeleton} />}
            </Grid>
            <Grid item>
              <ControlledCurrencyInput
                id="CurrentAdvance-Input"
                label="Valor a abonar"
                control={control}
                name="value"
                helperText={errors && errors.value && errors.value.message}
                error={errors && Boolean(errors.value)}
                thousandSeparator="."
                allowNegative={false}
                variant="outlined"
                className={classes.inputValueContainer}
                decimalSeparator={false}
                fullWidth
                isAllowed={({ floatValue }) =>
                  floatValue <= 999999999 || !floatValue
                }
                onBlur={() => {}}
                FormHelperTextProps={{
                  classes: { root: classes.inputValueText }
                }}
              />
            </Grid>
          </Grid>
          <Typography className={classes.textSmall}>
            El costo de generación es totalmente gratuito*
          </Typography>
        </form>
      </div>
    );
  }, [
    onSubmit,
    handleSubmit,
    errors,
    classes,
    control,
    isMobileSize,
    description,
    maxValue,
    currentDebtValueExist,
    loading,
    isParentLoading
  ]);

  // * RENDER
  return (
    <BaseDialog
      open={open}
      loading={loading || isParentLoading}
      handleClose={onClose}
      title={title}
      actions={renderActions}
      content={renderContent}
      fullScreen={isMobileSize}
      contentSize="small"
      TransitionComponent={isMobileSize ? SlideUpTransition : undefined}
    />
  );
};

const useStyles = makeStyles(theme => ({
  content: {
    display: 'center',
    flexDirection: 'column',
    alignItems: 'flex'
  },
  text: {
    fontSize: 14,
    color: theme.palette.text.primary,
    width: '100%',
    display: 'center'
  },
  contractForm: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  contractInput: {
    width: 250,
    marginTop: theme.spacing(4)
  },
  textSmall: {
    fontSize: 10,
    color: theme.palette.text.blackLight,
    marginTop: theme.spacing()
  },
  maxValueText: {
    marginBottom: theme.spacing(2)
  },
  lightBold: {
    fontWeight: 500
  },
  inputValueText: {
    fontSize: 11
  },
  dialogDescription: {
    fontSize: 16,
    color: theme.palette.text.primary,
    width: '100%'
  },
  skeleton: {
    margin: theme.spacing(0.5, 0, 0),
    height: 14,
    width: 200,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      margin: theme.spacing(1, 0)
    }
  },
  textHeight: {
    height: 35,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      height: 45,
      padding: theme.spacing(2, 0, 1.5)
    }
  },
  marginTop: {
    marginTop: theme.spacing(2)
  },
  marginBottom: {
    marginBottom: theme.spacing(2)
  }
}));

export default ValuePartialPaymentStep;
